import React from 'react';
import { Card } from '@/components/ui/card';
import {
  getQuarterIndexChange,
  getYearIndexChange,
  transformPriceHistoryData,
} from '@/utils/charts';
import { ChartLine, MapPin /* Tag */ } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { getPropertyAddress } from '@/utils/properties';
import { Skeleton } from '@/components/ui/skeleton';

const ValueEstimateOverview = ({
  allProperties,
  valueIncreases,
  valueEstimates,
  isBlurred = true,
}) => {
  function splitNumberParts(num) {
    const [integer, decimal = '00'] = String(num).split('.');
    const fixedDecimal = decimal.padEnd(2, '0').slice(0, 2);

    return {
      integer,
      decimalsExceptLast: fixedDecimal[0],
      lastDecimal: fixedDecimal[1],
    };
  }

  if (!allProperties?.length || !valueIncreases || !valueEstimates)
    return <Skeleton className="bg-secondary h-[372px] md:h-[239px] w-full" />;

  return (
    <div>
      <h3 className="text-[18px] md:text-xl font-medium mb-[13px]">
        Verdi & marked
      </h3>
      <Card className="rounded-[5px] bg-[#E6E6E645] md:bg-white py-3 md:py-5 px-5 md:px-7 text-[#333] shadow-none">
        {allProperties.map((property) => {
          if (
            !valueIncreases.find(
              (el) => el.boligmappaNumber === property.boligmappaNumber
            ).data.length ||
            !valueEstimates.find(
              (el) => el.boligmappaNumber === property.boligmappaNumber
            )?.valueEstimate
          ) {
            return (
              <div
                key={property.id}
                className="flex flex-col justify-between w-full gap-2 border-b last:border-b-0 pt-5"
              >
                <div>
                  <h4 className="text-base font-medium">
                    {getPropertyAddress(property)}
                  </h4>
                  <p className="text-[15px]">
                    {property.address.postalCode || ''}
                    {(property.address.postalPlace &&
                      ` · ${property.address.postalPlace}`) ||
                      ''}
                  </p>
                </div>
                <p>
                  Vår modell lykkes ikke i å gi et verdiestimat for akkurat
                  denne eiendommen. Ta kontakt via våre hjelpesider for å finne
                  en løsning
                </p>
                <div className="flex items-center justify-center pb-2">
                  <Button
                    className="font-normal text-base text-inherit"
                    variant="link"
                    asChild
                  >
                    <a
                      href="https://support.boligmappa.no"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Hjelpesider ›
                    </a>
                  </Button>
                </div>
              </div>
            );
          }
          const transformedData = transformPriceHistoryData({
            priceHistoryData: valueIncreases.find(
              (el) => el.boligmappaNumber === property.boligmappaNumber
            ),
            years: 5,
          });
          const quarterChange =
            transformedData?.data &&
            getQuarterIndexChange(transformedData?.data);
          const yearChange = getYearIndexChange(transformedData?.data);
          const sign = quarterChange >= 0;
          const displayPrice = (
            valueEstimates.find(
              (el) => el.boligmappaNumber === property.boligmappaNumber
            )?.valueEstimate / 1000000
          ).toFixed(2);
          const { integer, decimalsExceptLast, lastDecimal } =
            splitNumberParts(displayPrice);
          return (
            <div
              key={property.id}
              className="pt-5 first:pt-0 border-b last:border-b-0"
            >
              {allProperties.length > 1 && (
                <>
                  <h4 className="text-base font-medium">
                    {getPropertyAddress(property)}
                  </h4>
                  <p className="text-[15px]">
                    {property.address.postalCode || ''}
                    {(property.address.postalPlace &&
                      ` · ${property.address.postalPlace}`) ||
                      ''}
                  </p>
                </>
              )}
              <div className="flex flex-col md:flex-row justify-between w-full py-5 gap-10">
                <div className="flex gap-4">
                  <div className="flex items-center justify-center p-[15px] bg-[#fafafa] w-[48px] h-[48px] rounded-full">
                    <ChartLine color="red" />
                  </div>
                  <div>
                    <h4 className="flex items-center leading-[24px] text-2xl">
                      {integer},
                      {isBlurred ? (
                        <>
                          <span className="relative mr-[1px] after:absolute after:bg-[#d9d9d9] after:rounded-[2px] after:w-full after:h-full after:left-0 after:top-0">
                            ?
                          </span>
                          <span className="relative after:absolute after:bg-[#d9d9d9] after:rounded-[2px] after:w-full after:h-full after:left-0 after:top-0">
                            ?
                          </span>
                        </>
                      ) : (
                        <>
                          {decimalsExceptLast}
                          {lastDecimal}
                        </>
                      )}
                      &nbsp;mill.
                    </h4>
                    <p className="text-sm md:text-base opacity-[65%]">
                      Estimert verdi på boligen
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center justify-center p-[15px] bg-[#fafafa] w-[48px] h-[48px] rounded-full">
                    <MapPin color="red" />
                  </div>
                  <div>
                    <h4 className="flex items-center leading-[24px] text-2xl">
                      {isBlurred ? (
                        <>
                          {(+quarterChange).toFixed(0)},
                          <span className="relative mr-[1px] after:absolute after:bg-[#d9d9d9] after:rounded-[2px] after:w-full after:h-full after:left-0 after:top-0">
                            ?
                          </span>
                          <span className="relative after:absolute after:bg-[#d9d9d9] after:rounded-[2px] after:w-full after:h-full after:left-0 after:top-0">
                            ?
                          </span>
                        </>
                      ) : (
                        <>
                          {sign}
                          {quarterChange}
                        </>
                      )}
                      %
                    </h4>
                    <p className="text-sm md:text-base opacity-[65%]">
                      Utvikling ditt område, siste 3 mnd
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center justify-center p-[15px] bg-[#fafafa] w-[48px] h-[48px] rounded-full">
                    <MapPin color="red" />
                  </div>
                  <div>
                    <h4 className="flex items-center leading-[24px] text-2xl">
                      {isBlurred ? (
                        <>
                          {(+yearChange).toFixed(0)},
                          <span className="relative mr-[1px] after:absolute after:bg-[#d9d9d9] after:rounded-[2px] after:w-full after:h-full after:left-0 after:top-0">
                            ?
                          </span>
                          <span className="relative after:absolute after:bg-[#d9d9d9] after:rounded-[2px] after:w-full after:h-full after:left-0 after:top-0">
                            ?
                          </span>
                        </>
                      ) : (
                        <>
                          {sign}
                          {yearChange}
                        </>
                      )}
                      %
                    </h4>
                    <p className="text-sm md:text-base opacity-[65%]">
                      Utvikling ditt område, siste år
                    </p>
                  </div>
                </div>
              </div>
              {!isBlurred && (
                <div className="flex items-center justify-center pb-2">
                  <Button
                    className="font-normal text-base text-inherit"
                    variant="link"
                    asChild
                  >
                    <Link
                      to={`/dashboard/${property?.boligmappaNumber}/financials`}
                    >
                      Flere tall ›
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {isBlurred && (
          <div className="flex items-center mt-5">
            <p className="text-[15px]">
              <b>Vil du ha nøyaktige tall?</b> Med Boligmappa+ får du
              markedsledende, oppdaterte verdiestimat på din bolig, og mye mer!
            </p>
            <Button
              className="font-normal bg-[#D9D9D9] hover:bg-[#9f9f9f] hover:text-white"
              variant="secondary"
              asChild
            >
              <Link to="/personal-area/dashboard/boligmappa-plus">
                Oppgrader
              </Link>
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ValueEstimateOverview;
